<template>
  <div id="main-container">
    <dir class="title"> {{ sectorRef }} {{ $t('seatViewer.title') }} </dir>
    <v-card class="editor-section">
      <div ref="container" class="konva-container"></div>
      <div class="btn-section">
        <v-btn icon="mdi-minus" @click="zoomWithBtn()"/>
        <v-btn icon="mdi-plus" @click="zoomWithBtn(true)"/>
      </div>
    </v-card>
  </div>
</template>

<script>
import { Stage, Layer, Image, Group, Text, Tag, Label } from 'konva';
import { mapGetters } from 'vuex';

export default {
  name: 'KonvaViewer',
  props: {
    sectorRef: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      editorSizes: {
        width: undefined,
        height: undefined,
      },
      seats: undefined,
      stage: null,
      layer: null,
      tooltip: undefined,
    }
  },
  mounted() {
    // set editor(stage) size, responsive with screen size
    this.setEditorSizes();

    // define konva stage
    this.stage = new Stage({
      container: this.$refs.container,
      width: this.editorSizes.width,
      height: this.editorSizes.height,
      draggable: true,
      name: 'Stage',
    });

    // create layer
    this.layer = new Layer({});

    // load image for background
    this.setImageBackground();

    // add layer to stage
    this.stage.add(this.layer);

    // event handling for konva stage
    document.addEventListener('mousedown', (e) => { 
      if(e.button === 0) this.stage.draggable(false); 
    });
    document.addEventListener('mouseup', (e) => { 
        if(e.button === 0) this.stage.draggable(true); 
    });
    this.stage.on('wheel', this.zoom);

    // load sectors
    setTimeout(() => {
      this.seats = this.getSeats;
      this.loadSeats();
    }, 500);

    this.tooltip = new Label({
        opacity: 1,
        visible: false,
      });

    this.tooltip.add(
      new Tag({
        fill: 'blue',
        pointerDirection: 'down',
        pointerWidth: 10,
        pointerHeight: 10,
        lineJoin: 'round',
        shadowColor: 'blue',
        shadowBlur: 10,
        shadowOffsetX: 10,
        shadowOffsetY: 10,
        shadowOpacity: 0.2,
      })
    );

    this.tooltip.add(
      new Text({
        text: 'Tooltip pointing down',
        fontFamily: 'Calibri',
        fontSize: 18,
        padding: 5,
        fill: 'white',
      })
    );

    this.layer.add(this.tooltip);
  },
  methods: {
    loadSeats() {
      for (const current of this.seats) {
        this.drawSeat(current);
      }
    },

    // this method load shapes with calculated attributes
    drawSeat(current) {
      const localeScaleX = this.layer.find(`#image`)[0].attrs.width / current.imageSizes.width;
      const localeScaleY = this.layer.find(`#image`)[0].attrs.height / current.imageSizes.height;
      let seat = new Group({
        name: 'Seat',
        draggable: false,
        x: 0,
        y: 0,
        height: 20,
        width: 15,
        cornerRadius: 0,
        rowIndex: current.row,
        colIndex: current.col,
        number: current.number,
      });
      seat.clearCache();
      
      seat.on('mousemove', () => {
        let mousePos = this.stage.getRelativePointerPosition();
        this.tooltip.position({
          x: mousePos.x + 0,
          y: mousePos.y + 0,
        });
        this.tooltip.children[1].text(`sample tooltip for ${seat.attrs.number} seat`);
        this.tooltip.show();
        this.tooltip.moveToTop();
      });

      seat.on('mouseout', () => {
        this.tooltip.hide();
      });

      Image.fromURL('/images/seat.svg', (image) => {
        image.setAttrs({
          x: 0,
          y: 0,
          height: 20,
          width: 15,
          cornerRadius: 0,
          name: 'Image',
          id: 'image',
          rowIndex: current.row,
          colIndex: current.col,
          number: current.number,
        });

        var simpleText = new Text({
          x: (image.width() / 2) - (Math.abs(seat.attrs.number).toString().length) * 2,
          y: (image.height() / 2) - 6,
          text: current.number,
          fontSize: 8,
          fontFamily: 'Calibri',
          fill: 'white',
        });

        seat.add(image);
        seat.add(simpleText);

        seat.absolutePosition({
          x: current.x * localeScaleX,
          y: current.y * localeScaleY,
        })
        seat.scaleX(current.scaleX * localeScaleX);
        seat.scaleY(current.scaleY * localeScaleY);
        seat.rotation(current.rotation);
        this.layer.add(seat);
      });
    },

    // this method set konva editor size, when page mount
    setEditorSizes() {
      const main = document.getElementById('main-container');
      this.editorSizes.width = main.offsetWidth;
      this.editorSizes.height = main.offsetHeight - 110;
      if (window.innerWidth < 700) {
        this.editorSizes.height = main.offsetHeight - 400;
      }
    },

    // this method load image to background or add new image if we select upload option
    setImageBackground() {
      Image.fromURL(`/images/${this.getCurrentSeatImage}`, (image) => {
        const imagePlaceSizeRatioWidth = this.editorSizes.width / image.width();
        const imagePlaceSizeRatioHeight = this.editorSizes.height / image.height();
        const ratio = imagePlaceSizeRatioHeight < imagePlaceSizeRatioWidth ? imagePlaceSizeRatioHeight : imagePlaceSizeRatioWidth;
        image.setAttrs({
          opacity: 0.3,
          x: 0,
          y: 0,
          height: image.height() * ratio,
          width: image.width() * ratio,
          cornerRadius: 20,
          name: 'Image',
          id: 'image'
        });
        this.layer.add(image);
      });
    },

    // this method hanlde zoom with mouse middle button
    zoom(e) {
      let scaleBy = 1.2;
      e.evt.preventDefault();
      let oldScale = this.stage.scaleX();
      let pointer = this.stage.getRelativePointerPosition();
      let mousePointTo = {
        x: (pointer.x - this.stage.x()) / oldScale,
        y: (pointer.y - this.stage.y()) / oldScale,
      };
      let direction = e.evt.deltaY > 0 ? -1 : 1;
      if (e.evt.ctrlKey) {
        direction = -direction;
      }
      let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      this.stage.scale({ x: newScale, y: newScale });
      let newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };
      this.stage.position(newPos);
    },

    // this method hanlde zoom with implemented buttons
    zoomWithBtn(isIn = false) {
      let scaleBy = 1.2;
      let oldScale = this.stage.scaleX();
      let center = {
        x: this.stage.width() / 2,
        y: this.stage.height() / 2,
      };
      let relatedTo = {
        x: (center.x - this.stage.x()) / oldScale,
        y: (center.y - this.stage.y()) / oldScale,
      };
      let newScale = isIn ? oldScale * scaleBy : oldScale / scaleBy;
      this.stage.scale({
        x: newScale,
        y: newScale
      });
      let newPos = {
        x: center.x - relatedTo.x * newScale,
        y: center.y - relatedTo.y * newScale,
      };
      this.stage.position(newPos);
      this.stage.batchDraw();
    },
  },
  computed: {
    ...mapGetters({
      getSeats: 'siteSettings/getSeats',
      getCurrentSeatImage: 'siteSettings/getCurrentSeatImage'
    })
  }
}
</script>

<style lang="scss" scoped>
#main-container {
  height: calc(100% - 20px);
  .title {
    margin-top: 16px;
    margin-bottom: 16px;
    font-size: 22px;
    font-weight: 550;
  }
  .actions {
    display: flex;
    align-items: center;
    margin: 16px 0;
    .sectors {
      max-height: 56px;
      max-width: 160px;
      margin-right: 24px;
    }
    .remove-button {
      margin-right: 24px;
    }
    .cancel-edit-button {
      margin-right: 24px;
    }
    .append-object-section {
      display: flex;
      align-items: center;
      p {
        margin-right: 12px;
      }
      .object-btn {
        margin: 0 4px;
      }
    }
  }
  .editor-section {
    position: relative;
    .konva-container {
    }
    .btn-section {
      position: absolute;
      bottom: 18px;
      right: 18px;
      width: 110px;
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>